/**
 * Pure transform: given a single view's lineage response, derive the delta to merge
 * into the current lineage map. No React / Redux / side-effects.
 *
 * The view's discovered source tables are merged into the SAME database node as extra
 * `data.tables[]` slots (not new standalone nodes). The view→source column relationships
 * become INTRA-NODE mappings (sourceNodeId === targetNodeId === dbNodeId, different table
 * slots), so in data view the one node expands into a cluster of cards — the view card
 * plus one card per discovered source — wired by element-level mappings.
 *
 * Returns {tablesToAdd, mappingsToAdd, viewElementsToMerge}. Every field is an array;
 * all-empty = nothing to do (caller must NOT commit that as a history entry).
 */

import {v4 as uuidv4} from 'uuid';

/**
 * Resolve the canvas element id for a columnId inside a given table slot on the DB node.
 * Checks the DB node's existing slot first, then the pending (about-to-be-added) elements
 * keyed by `${dbNodeId}:${tableSlotId}`.
 */
const resolveElementId = (dbNode, tableSlotId, columnId, pendingElements) => {
  if (!columnId) return null;
  const pending = (pendingElements && pendingElements[`${dbNode.id}:${tableSlotId}`]) || [];
  const pendingMatch = pending.find((e) => e.columnId === columnId);
  if (pendingMatch) return pendingMatch.id;
  const slot = (dbNode.data.tables || []).find((t) => t.id === tableSlotId);
  const el = slot && (slot.dataElements || []).find((e) => e.columnId === columnId);
  return el ? el.id : null;
};

/**
 * deriveViewLineage — main export.
 *
 * @param {object}   responseView    Single view object from response.views (pre-filtered by caller)
 * @param {string}   dbNodeId        Canvas node id of the DB node holding the view table
 * @param {string}   viewTableId     Table slot id on that DB node that holds the view
 * @param {object[]} existingNodes   map.nodes at call time
 * @param {object[]} existingMappings map.mappings at call time
 */
export const deriveViewLineage = (
    responseView,
    dbNodeId,
    viewTableId,
    existingNodes,
    existingMappings,
) => {
  const empty = {tablesToAdd: [], mappingsToAdd: [], viewElementsToMerge: []};

  if (!responseView || !responseView.sources || responseView.sources.length === 0) {
    return empty;
  }

  const sources = responseView.sources;
  const dbNode = existingNodes.find((n) => n.id === dbNodeId);
  if (!dbNode) return empty;

  const tablesToAdd = [];
  const mappingsToAdd = [];
  const viewElementsToMerge = [];

  // Elements about to be added, keyed by `${dbNodeId}:${slotId}`, so mappings can resolve
  // ids for source slots created in this same batch.
  const pendingElements = {};

  // Dedupe mapping fingerprints against existing + within-batch. sourceNodeId/targetNodeId
  // are both dbNodeId now, so the key naturally scopes to intra-node mappings.
  const existingMappingKeys = new Set(
      existingMappings.map((m) =>
        `${m.sourceNodeId}:${m.sourceTableId}:${m.sourceElementId}:${m.targetTableId}:${m.targetElementId}`),
  );

  // The view table slot (target of every mapping) + its existing columns by columnId, so we
  // resolve targetElementId to an existing element and only merge truly-new view columns.
  const viewTableObj = (dbNode.data.tables || []).find((t) => t.id === viewTableId);
  const existingViewElsByColId = new Map(
      ((viewTableObj && viewTableObj.dataElements) || [])
          .filter((e) => e.columnId)
          .map((e) => [e.columnId, e]),
  );

  // Collect the view's own columns for viewElementsToMerge (only truly new ones).
  const viewColSeen = new Set();
  const pendingViewEls = new Map(); // columnId → element
  sources.forEach((src) => {
    (src.columnMappings || []).forEach((cm) => {
      if (cm.viewColumnId && !viewColSeen.has(cm.viewColumnId)) {
        viewColSeen.add(cm.viewColumnId);
        if (!existingViewElsByColId.has(cm.viewColumnId)) {
          const el = {
            id: `el-${uuidv4()}`,
            name: cm.viewColumn || '',
            displayName: cm.viewColumn || '',
            dataType: '',
            columnId: cm.viewColumnId,
            isCde: false,
          };
          viewElementsToMerge.push(el);
          pendingViewEls.set(cm.viewColumnId, el);
        }
      }
    });
  });

  sources.forEach((src) => {
    const {sourceTableId, sourceSchema, sourceTable, columnMappings = []} = src;

    // Same-node dedupe: reuse an existing slot on the DB node (by objectId, truthy only),
    // then a slot already queued in this batch, else create a new slot. This keeps
    // re-expansion idempotent and prevents mappings pointing at ids that never land.
    let sourceSlotId;
    const existingSlot = sourceTableId &&
      (dbNode.data.tables || []).find((t) => t.objectId === sourceTableId);
    const batchSlot = sourceTableId &&
      tablesToAdd.find((t) => t.objectId === sourceTableId);

    if (existingSlot) {
      sourceSlotId = existingSlot.id;
    } else if (batchSlot) {
      sourceSlotId = batchSlot.id;
    } else {
      sourceSlotId = `tbl-${uuidv4()}`;
      const tableElements = [];
      columnMappings.forEach((cm) => {
        if (!cm.sourceColumnId || cm.extractionMethod === 'SQL_PARSE_STAR') return;
        if (tableElements.find((e) => e.columnId === cm.sourceColumnId)) return;
        tableElements.push({
          id: `el-${uuidv4()}`,
          name: cm.sourceColumn || '',
          displayName: cm.sourceColumn || '',
          dataType: '',
          columnId: cm.sourceColumnId,
          isCde: false,
        });
      });
      pendingElements[`${dbNodeId}:${sourceSlotId}`] = tableElements;
      tablesToAdd.push({
        id: sourceSlotId,
        name: sourceTable || (sourceSchema ? `${sourceSchema}.${sourceTable}` : ''),
        objectId: sourceTableId || null,
        dataElements: tableElements,
      });
    }

    // SQL_PARSE_STAR only: slot added, no element mappings (table-level knowledge only).
    // ponytail: full column resolution via /dataDictionary/columns/:objectId if STAR ever needs element mappings
    const hasStarOnly = columnMappings.length > 0 &&
      columnMappings.every((cm) => cm.extractionMethod === 'SQL_PARSE_STAR');
    if (hasStarOnly) return;

    columnMappings.forEach((cm) => {
      // Skip null source (EXPRESSION / computed) and SQL_PARSE_STAR rows.
      if (!cm.sourceColumnId || !cm.viewColumnId) return;
      if (cm.extractionMethod === 'SQL_PARSE_STAR') return;

      const sourceElementId = resolveElementId(dbNode, sourceSlotId, cm.sourceColumnId, pendingElements);
      if (!sourceElementId) return;

      const existingViewEl = existingViewElsByColId.get(cm.viewColumnId);
      const pendingViewEl = pendingViewEls.get(cm.viewColumnId);
      const targetElementId = existingViewEl ? existingViewEl.id : (pendingViewEl ? pendingViewEl.id : null);
      if (!targetElementId) return;

      const key = `${dbNodeId}:${sourceSlotId}:${sourceElementId}:${viewTableId}:${targetElementId}`;
      if (existingMappingKeys.has(key)) return;
      existingMappingKeys.add(key);

      mappingsToAdd.push({
        id: `map-${uuidv4()}`,
        edgeId: `self-${dbNodeId}`, // intra-node sentinel — no real edge in map.edges
        sourceNodeId: dbNodeId,
        sourceTableId: sourceSlotId,
        sourceElementId,
        targetNodeId: dbNodeId,
        targetTableId: viewTableId,
        targetElementId,
        direction: 'forward',
        transformation: cm.extractionMethod || '',
        label: '',
        data: {autoDerived: true},
      });
    });
  });

  return {tablesToAdd, mappingsToAdd, viewElementsToMerge};
};
